import React, { ReactNode, useMemo, useState } from 'react';
import { Avatar, Box, Button, ButtonGroup, Grid, IconButton, Typography } from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { DateTime } from 'luxon';
import _, { Dictionary } from 'lodash';
import ListHeader from '../components/listHeader/ListHeader';
import FilterSidebar from '../components/common/filters/FilterSidebar';
import DrawerLink from '../components/common/drawers/ModuleDrawer/DrawerLink';
import { EventTile } from '../components/dashboard/EventsPanel/EventTile';
import { useIsMobile } from '../hooks/useIsMobile';
import { useGetAllPreferences } from '../services/react-query/preferences';
import { useGetScheduledEmployeeEvents } from '../services/react-query/employees';
import { EmployeesScheduleFilters } from '../utility/employee';
import { humanizeDate } from '../utility/dateUtils';
import { UserConfigurableModule } from '../types/User';
import { FilterState } from '../components/common/filters/interfaces';
import { PREFERENCES_VALID_KEYS } from '../types/Preference';
import { Event } from '../types/Event';
import { headerTypographyProps, listPageLayoutStyles } from '../styles/sxStyling';
import {
  ROW_HEIGHT,
  borderStyle,
  contentContainerProps,
  headerSx,
  employeeHeaderSx,
  eventHeaderSx,
  dateCellSx,
  calendarWrapperSx,
  calendarScrollableContentSx,
} from '../components/employeeSchedule/employeesScheduleStyles';

interface IEventData {
  event: Event;
  startCol: number;
  endCol: number;
}

interface IRowData {
  employeeUuid: string;
  employeeLabel: string;
  employeeAvatarUrl?: string;
  events: IEventData[];
}

// column bounded between Mon-Sun
const boundedCol = (col: number) => Math.min(8, Math.max(col, 1));

// the starting col is where the event actually starts from (inclusive)
// the ending col is where the event ends (exclusive)
// if the event goes into a given day even for 1 second that day gets included
const calcEventCols = (weekStart: DateTime, event: Event): { startCol: number; endCol: number } => {
  const [eventStart, eventEnd] = _.map([event.start_at, event.end_at], (d) => DateTime.fromISO(d));
  const [startDayDiff, endDayDiff] = _.map([eventStart, eventEnd], (d) => d.diff(weekStart, ['hours']).hours / 24);

  return {
    startCol: boundedCol(1 + Math.floor(startDayDiff)),
    endCol: boundedCol(1 + Math.ceil(endDayDiff)),
  };
};

// Returns true if more than one event occupies the same calendar column (day)
const overlappingEvents = (events: IEventData[]): boolean =>
  _.some(_.range(1, 8), (col) => _.filter(events, (event) => event.startCol <= col && col < event.endCol).length > 1);

const DatesHeader: React.FC<{ weekStart: DateTime }> = ({ weekStart }) => (
  <>
    <Grid size={1} sx={employeeHeaderSx}>
      <Typography>Employees</Typography>
    </Grid>

    <Grid {...contentContainerProps} sx={{ ...headerSx, p: 0, display: 'flex', flexDirection: 'column' }}>
      <Box sx={eventHeaderSx}>
        <Typography fontWeight="500">Events</Typography>
      </Box>

      <Box sx={{ display: 'flex', height: ROW_HEIGHT }}>
        {_.map(_.range(0, 7), (dayDiff) => {
          const date = weekStart.plus({ day: dayDiff });

          return (
            <Box key={dayDiff} sx={dateCellSx}>
              <Typography variant="caption" title={humanizeDate(date.toISO())}>
                {`${date.weekdayShort}, ${date.day}`}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Grid>
  </>
);

const EmployeesSchedule: React.FC = () => {
  const { isMobile } = useIsMobile();

  const [filterState, setFilterState] = useState<FilterState>(undefined);
  const [weekStart, setWeekStart] = useState<DateTime<true>>(DateTime.now().startOf('week'));
  const [multiRow, setMultiRow] = useState<Dictionary<boolean>>({});

  const weekEnd = useMemo(() => weekStart.plus({ days: 7 }), [weekStart]);

  const { data: preferenceData } = useGetAllPreferences({
    params: {
      filters: {
        keys: [PREFERENCES_VALID_KEYS.EMPLOYEE_ROLE, PREFERENCES_VALID_KEYS.EMPLOYEE_TITLE],
      },
    },
  });

  const { data: employeeEvents, error: employeeEventsError } = useGetScheduledEmployeeEvents({
    params: {
      date_range: [weekStart.toISO(), weekEnd.toISO()],
      filters: filterState,
    },
    enabled: true,
  });

  const rowData: IRowData[] = useMemo(() => {
    if (!employeeEvents) return [];

    const eventsByEmployeeUuid = _.groupBy(employeeEvents.event_employees, 'employee_uuid');

    return _.map(employeeEvents.employees, (employee) => ({
      employeeUuid: employee.uuid,
      employeeLabel: employee.display_name,
      employeeAvatarUrl: employee.avatar_url,
      events: _(eventsByEmployeeUuid[employee.uuid])
        .map((employeeEvent) => ({
          event: employeeEvent.event,
          ...calcEventCols(weekStart, employeeEvent.event),
        }))
        .sortBy('startCol')
        .value(),
    }));
  }, [employeeEvents, weekStart]);

  const toggleMultiRow = (uuid: string) =>
    setMultiRow((prev) => ({
      ...prev,
      [uuid]: !prev[uuid],
    }));

  if (isMobile) {
    return (
      <Box sx={{ alignContent: 'center', height: '100%' }}>
        <Typography {...headerTypographyProps} whiteSpace="wrap" textAlign="center">
          We&apos;re not able to correctly show this page at this size.
          <br />
          <br />
          Please use a device with a larger screen.
        </Typography>
      </Box>
    );
  }

  // Renders event tiles across the weekly grid, handling gaps and optional multi-row wrapping
  const renderEvents = (events: IEventData[], multi: boolean) => {
    const children: ReactNode[] = [];

    // Work on a mutable copy so we can remove events as they are rendered
    let remainingEvents = _.cloneDeep(_.uniq(events));
    let col = 1;

    // Continue until all events are placed in the grid
    while (!_.isEmpty(remainingEvents)) {
      // Find an event that starts in the current column
      const eventData = _.find(remainingEvents, { startCol: col });

      if (_.isNil(eventData)) {
        // No event starts here — render an empty day cell
        children.push(<Grid size={1} key={`empty-${col}`} />);
        col++;
      } else {
        // Render the event spanning the required number of columns
        const size = eventData.endCol - eventData.startCol;

        children.push(
          <Grid size={size} key={eventData.event.uuid}>
            <EventTile {...eventData.event} />
          </Grid>
        );

        // Remove the rendered event and advance the column pointer
        remainingEvents = _.reject(remainingEvents, { event: { uuid: eventData.event.uuid } });
        col += size;
      }

      // Handle row overflow: wrap to next row if multi-row is enabled
      if (col > 7) {
        if (multi) {
          col = 1;
        } else {
          break;
        }
      }
    }

    return children;
  };

  const renderRow = (item: IRowData) => (
    <Grid container key={item.employeeUuid} columns={8} size={8} sx={{ '& > .MuiGrid-root': { pb: 1, borderBottom: borderStyle } }}>
      <Grid size={1} sx={{ borderRight: borderStyle, pl: 1, fontSize: '0.8rem' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pt: 1 }}>
            <Avatar src={item.employeeAvatarUrl} alt="avatar" sx={{ width: 20, height: 20 }} />
            <DrawerLink module={UserConfigurableModule.EMPLOYEES} uuid={item.employeeUuid}>
              {item.employeeLabel}
            </DrawerLink>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            {overlappingEvents(item.events) && (
              <IconButton onClick={() => toggleMultiRow(item.employeeUuid)} sx={{ width: 'fit-content', p: 0.5, '& svg': { fontSize: '1rem' } }}>
                {multiRow[item.employeeUuid] ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            )}
          </Box>
        </Box>
      </Grid>

      <Grid {...contentContainerProps} spacing={0.5}>
        {renderEvents(item.events, Boolean(multiRow[item.employeeUuid]))}
      </Grid>
    </Grid>
  );

  return (
    <Box>
      <ListHeader title="Employees Schedule" rightActions={null} />

      <Box sx={listPageLayoutStyles}>
        <FilterSidebar
          moduleFilters={EmployeesScheduleFilters(preferenceData ?? [])}
          module={UserConfigurableModule.EMPLOYEES_SCHEDULE}
          showSearch={false}
          activeFilter={filterState}
          setActiveFilter={setFilterState}
        />

        <Box sx={calendarWrapperSx}>
          <Box sx={{ display: 'flex', alignItems: 'center', p: 1, borderBottom: borderStyle, bgcolor: 'var(--table-header-bg)' }}>
            <ButtonGroup size="small">
              <Button sx={{ height: '24px' }} onClick={() => setWeekStart(weekStart.minus({ weeks: 1 }))}>
                Prev
              </Button>
              <Button sx={{ height: '24px' }} onClick={() => setWeekStart(DateTime.now().startOf('week'))}>
                Today
              </Button>
              <Button sx={{ height: '24px' }} onClick={() => setWeekStart(weekStart.plus({ weeks: 1 }))}>
                Next
              </Button>
            </ButtonGroup>

            <Typography variant="h5" sx={{ width: '100%', textAlign: 'center', fontSize: '0.9rem' }}>
              {`${humanizeDate(weekStart.toISO())} - ${humanizeDate(weekEnd.toISO())}`}
            </Typography>
          </Box>

          {employeeEventsError ? (
            <Typography align="center" variant="h6" sx={{ pt: 6 }}>
              {employeeEventsError.message}
            </Typography>
          ) : (
            <Box sx={calendarScrollableContentSx}>
              <Grid container columns={8}>
                <DatesHeader weekStart={weekStart} />
                {_.map(rowData, renderRow)}
              </Grid>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default EmployeesSchedule;
